import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { UserActions } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { finalize, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import { AppActionTypes } from 'src/app/store/actions';
import { getCropYear } from 'src/app/store/selectors';

import * as CropAidActions from './crop-aid.actions';
import { SiteState } from '../../shared/models';
import { ApiService } from '../../shared/services/api.service';
import { UnitOfMeasurementEnum } from '../crop-aid.model';
import { CropAidApiService } from '../services/crop-aid-api.service';

@Injectable()
export class CropAidEffects {
  protected readonly store = inject<Store<SiteState>>(Store);
  protected readonly actions$ = inject(Actions);
  protected readonly api = inject(ApiService);
  protected readonly router = inject(Router);
  protected readonly cropAidApi = inject(CropAidApiService);

  userInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      switchMap(() => [CropAidActions.getGenericCropAids()]),
    ),
  );

  loadCustomerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.LoadCustomerData),
      switchMap(() => [CropAidActions.getProprietaryCropAids()]),
    ),
  );

  getGenericCropAids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropAidActions.getGenericCropAids),
      switchMap((_) =>
        this.cropAidApi.getGenericCropAids().pipe(
          map((cropAids) => {
            const mapped = [
              ...cropAids.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 0)),
            ].map((cropAid) => ({
              ...cropAid,
              /* eslint-disable-next-line */
              unitOfMeasurement: (<any>UnitOfMeasurementEnum)[cropAid.unitOfMeasurement],
            }));
            return CropAidActions.setGenericCropAids({ cropAids: mapped });
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  getProprietaryCropAids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropAidActions.getProprietaryCropAids),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) =>
        this.cropAidApi.getProprietaryCropAids(cropYear).pipe(
          map((cropAids) => {
            const mapped = [
              ...cropAids.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 0)),
            ].map((cropAid) => ({
              ...cropAid,
              /* eslint-disable-next-line */
              unitOfMeasurement: (<any>UnitOfMeasurementEnum)[cropAid.unitOfMeasurement],
            }));
            return CropAidActions.setProprietaryCropAids({ cropAids: mapped });
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );
}
